import {
  onboardingStepKindByKey,
  type OnboardingStatusDto,
  type OnboardingStepDto,
  type OnboardingStepKind,
} from '@osteopro/shared';
import { Badge, Button, Progress, cn } from '@osteopro/ui';
import { CheckCircle2, ChevronRight, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { checklistGroups, checklistProgress, ONBOARDING_STEP_PATHS } from '../../onboarding/steps.js';

const KIND_LABEL = {
  required: 'onboarding:checklist.kind.required',
  starter: 'onboarding:checklist.kind.starter',
  optional: 'onboarding:checklist.kind.optional',
} as const satisfies Record<OnboardingStepKind, string>;

const SECTION_LABEL = {
  required: 'onboarding:checklist.section.required',
  starter: 'onboarding:checklist.section.starter',
  optional: 'onboarding:checklist.section.optional',
} as const satisfies Record<OnboardingStepKind, string>;

/**
 * Corps de la liste « Mise en route », commun à la carte du tableau de bord et au panneau ouvert
 * depuis la barre latérale. L'état de chaque ligne vient du serveur, calculé depuis les données :
 * rien ne se coche à la main. Une étape réglée par le titulaire est montrée sans bouton.
 */
export function OnboardingChecklist({
  status,
  onOpen,
  requiredProgress = false,
}: {
  status: OnboardingStatusDto;
  onOpen: (path: string) => void;
  /**
   * La barre du haut ne compte que les étapes obligatoires. Les optionnelles gardent
   * un compteur à elles, sur le titre de leur groupe.
   */
  requiredProgress?: boolean;
}) {
  const { t } = useTranslation();
  const counted = requiredProgress
    ? status.steps.filter((step) => onboardingStepKindByKey[step.key] === 'required')
    : status.steps;
  const progress = checklistProgress(counted);

  return (
    <div className="space-y-4" data-testid="onboarding-checklist">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground" data-testid="text-onboarding-progress">
            {t('onboarding:checklist.progress', { done: progress.done, total: progress.total })}
          </span>
          <span className="font-medium tabular-nums">{progress.percent} %</span>
        </div>
        <Progress
          value={progress.percent}
          aria-label={t('onboarding:checklist.title')}
          data-testid="progress-onboarding"
        />
      </div>

      {checklistGroups(status.steps).map((group) => {
        const optionalCount = requiredProgress && group.kind === 'optional';
        const optionalProgress = checklistProgress(group.steps);
        return (
          <section key={group.kind} className="space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <p
                className="text-sm font-medium text-muted-foreground"
                data-testid={`text-onboarding-section-${group.kind}`}
              >
                {t(SECTION_LABEL[group.kind])}
              </p>
              {optionalCount && (
                <p
                  className="text-sm tabular-nums text-muted-foreground"
                  data-testid="text-onboarding-optional-progress"
                >
                  {t('onboarding:checklist.progress', {
                    done: optionalProgress.done,
                    total: optionalProgress.total,
                  })}
                </p>
              )}
            </div>
            <ul className="divide-y rounded-lg border">
              {group.steps.map((step) => (
                <ChecklistRow key={step.key} step={step} onOpen={onOpen} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function ChecklistRow({
  step,
  onOpen,
}: {
  step: OnboardingStepDto;
  onOpen: (path: string) => void;
}) {
  const { t } = useTranslation();
  const Icon = step.done ? CheckCircle2 : Circle;

  return (
    <li
      className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`row-onboarding-${step.key}`}
      data-done={step.done ? 'true' : 'false'}
    >
      <div className="flex min-w-0 items-start gap-3">
        <Icon
          className={cn(
            'mt-0.5 h-5 w-5 shrink-0',
            step.done ? 'text-success' : 'text-muted-foreground',
          )}
          aria-hidden
        />
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('text-sm font-medium', step.done && 'text-muted-foreground')}>
              {t(`onboarding:steps.${step.key}.title`)}
            </span>
            <StepKindBadge stepKey={step.key} />
          </div>
          {!step.done && (
            <p className="text-sm text-muted-foreground">
              {t(`onboarding:steps.${step.key}.description`)}
            </p>
          )}
        </div>
      </div>
      <StepAction step={step} onOpen={onOpen} />
    </li>
  );
}

function StepKindBadge({ stepKey }: { stepKey: OnboardingStepDto['key'] }) {
  const { t } = useTranslation();
  const kind = onboardingStepKindByKey[stepKey];
  return (
    <Badge
      variant={kind === 'required' ? 'secondary' : 'outline'}
      data-testid={`badge-onboarding-${kind}-${stepKey}`}
    >
      {t(KIND_LABEL[kind])}
    </Badge>
  );
}

function StepAction({
  step,
  onOpen,
}: {
  step: OnboardingStepDto;
  onOpen: (path: string) => void;
}) {
  const { t } = useTranslation();
  if (step.done) {
    return (
      <span
        className="shrink-0 text-sm text-muted-foreground"
        data-testid={`text-onboarding-done-${step.key}`}
      >
        {t(step.actionable ? 'onboarding:checklist.done' : 'onboarding:checklist.done_by_owner')}
      </span>
    );
  }
  if (!step.actionable) {
    return (
      <span
        className="shrink-0 text-sm text-muted-foreground"
        data-testid={`text-onboarding-owner-${step.key}`}
      >
        {t('onboarding:checklist.owner_pending')}
      </span>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      className="shrink-0 self-start sm:self-auto"
      onClick={() => {
        onOpen(ONBOARDING_STEP_PATHS[step.key]);
      }}
      data-testid={`button-onboarding-open-${step.key}`}
    >
      {t('onboarding:checklist.open')}
      <ChevronRight className="h-4 w-4" aria-hidden />
    </Button>
  );
}
